import React from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from "react-router-dom";
import { RuleException } from "@/types/RuleException";

interface RecentExceptionsSectionProps {
  exceptions: RuleException[];
  exceptionCount: number;
}

export const RecentExceptionsSection: React.FC<RecentExceptionsSectionProps> = ({
  exceptions,
  exceptionCount,
}) => {
  const navigate = useNavigate();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Recent Exceptions</CardTitle>
        <CardDescription>
          Latest exceptions requiring attention ({exceptionCount} total)
        </CardDescription>
      </CardHeader>
      <CardContent>
        {exceptions.length > 0 ? (
          <div className="space-y-4">
            {exceptions
              .slice(0, 10)
              .map((exception) => (
                <div
                  key={exception.ruleExceptionId}
                  className="flex items-start justify-between border-b pb-3 last:border-b-0"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <div className={`w-2 h-2 rounded-full ${
                        exception.status.toLowerCase() === 'critical' || exception.status.toLowerCase() === 'error' ? 'bg-red-500' :
                        exception.status.toLowerCase() === 'high' || exception.status.toLowerCase() === 'warning' ? 'bg-orange-500' :
                        exception.status.toLowerCase() === 'medium' || exception.status.toLowerCase() === 'pending' ? 'bg-yellow-500' :
                        'bg-blue-500'
                      }`}></div>
                      <p className="font-medium text-sm">
                        {exception.shortDescription}
                      </p>
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">
                      {exception.description}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span>Rule: {exception.ruleKey}</span>
                      <span>
                        Created: {new Date(exception.createdAt).toLocaleDateString()}
                      </span>
                      {exception.assignedTo && (
                        <span>Assigned to: {exception.assignedTo}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <Badge 
                      variant={
                        exception.status.toLowerCase() === 'critical' || exception.status.toLowerCase() === 'error' ? 'destructive' :
                        exception.status.toLowerCase() === 'high' || exception.status.toLowerCase() === 'warning' ? 'default' :
                        'secondary'
                      }
                      className="text-xs"
                    >
                      {exception.status}
                    </Badge>
                  </div>
                </div>
              ))}
            {exceptions.length > 10 && (
              <Button
                variant="link"
                size="sm"
                onClick={() => navigate("/exceptions")}
                className="mt-2 p-0 h-auto"
              >
                View all {exceptions.length} exceptions
              </Button>
            )}
          </div>
        ) : (
          <p className="text-muted-foreground">No exceptions found</p>
        )}
      </CardContent>
    </Card>
  );
}; 